import * as Sentry from "@sentry/react"
import React, {useEffect} from "react"
import ReactDOM from "react-dom/client"
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from "react-router-dom"

import {App} from "./App.tsx"
import fontCss from "./design/fonts/fonts.css?inline"
import {LandingPage} from "./entry-app/index.tsx"
import {API_BASE_URL, SENTRY_ENVIRONMENT, SKIP_LANDING_PAGE} from "./env.ts"
import {ChatWidget} from "./widget/ChatWidget.tsx"
import {
  ExpandWidgetInput,
  normalizeStarterBootstrapPayload,
} from "./widget/starterBootstrap.ts"
import {WIDGET_SLIDEOUT_WIDTH} from "./widget/styles.ts"

let rootContainer: HTMLDivElement | undefined
let widgetContainer: HTMLDivElement | undefined
let styleElement: HTMLStyleElement | undefined
let root: ReactDOM.Root | undefined
let widgetRoot: ReactDOM.Root | undefined

const SLIDEOUT_BODY_CLASS = "ri2-slideout-active"
const SLIDEOUT_WIDTH_VAR = "--ri2-slideout-width"
const MOBILE_BREAKPOINT = 768

const applySlideoutLayout = (): void => {
  if (window.ri2?.displayMode !== "slideout") return
  if (window.innerWidth < MOBILE_BREAKPOINT) return
  document.body.classList.add(SLIDEOUT_BODY_CLASS)
  document.documentElement.style.setProperty(
    SLIDEOUT_WIDTH_VAR,
    `${WIDGET_SLIDEOUT_WIDTH}px`,
  )
}

const removeSlideoutLayout = (): void => {
  document.body.classList.remove(SLIDEOUT_BODY_CLASS)
  document.documentElement.style.removeProperty(SLIDEOUT_WIDTH_VAR)
}

const initSentry = (): void => {
  if (SENTRY_ENVIRONMENT !== "development") {
    Sentry.init({
      dsn: "https://73f798bd169776b6bc7bc39ae2f6496e@o4506792914321408.ingest.sentry.io/4506813849927680",
      environment: SENTRY_ENVIRONMENT,
      integrations: [
        Sentry.reactRouterV6BrowserTracingIntegration({
          useEffect,
          useLocation,
          useNavigationType,
          createRoutesFromChildren,
          matchRoutes,
        }),
      ],
      // Performance Monitoring
      tracesSampleRate: 1.0,
      tracePropagationTargets: [API_BASE_URL],
      // Session Replay
      replaysSessionSampleRate: 0.1,
      replaysOnErrorSampleRate: 1.0,
      // Filtering
      beforeSend: (event, _hint) => {
        if (!event?.tags?.error_boundary) return null

        return event
      },
    })
  }
}

const ensureFonts = (): void => {
  if (!styleElement) {
    styleElement = document.createElement("style")
    styleElement.textContent = fontCss
    document.head.appendChild(styleElement)
  }
}

// ---- Full-screen app mode (legacy) ----

export const open = (initialMessage?: string): void => {
  if (root) {
    return
  }

  if (initialMessage) {
    // Pass via URL so LandingPage reads it with useSearchParams — no
    // dependency on window.ri2 timing.
    window.location.hash = `/ri2?hostMsg=${encodeURIComponent(initialMessage)}`
  }

  if (!rootContainer) {
    rootContainer = document.createElement("div")

    rootContainer.id = "ri2-root"
    document.body.appendChild(rootContainer)
  }

  // In testing, font imports seem to only work if they are in the
  // parent document, not inside the shadow DOM, so we have to add it
  // here instead of keeping it in <App /> with the rest of the
  // styles.
  ensureFonts()

  // Prevent the host page scrolling while our modal is open
  document.body.style.top = `-${window.scrollY}px`
  document.body.style.position = "fixed"

  initSentry()

  root = ReactDOM.createRoot(rootContainer)
  root.render(
    <React.StrictMode>
      <App onExit={close} />
    </React.StrictMode>,
  )
}

export const close = (): void => {
  if (!root) {
    return
  }

  root.unmount()
  root = undefined

  // Re-enable scrolling of the host page
  const scrollY = document.body.style.top
  document.body.style.position = ""
  document.body.style.top = ""
  window.scrollTo(0, parseInt(scrollY ?? "0") * -1)

  Sentry.close()
}

// ---- Chat widget mode (new default embed) ----

export const mountWidget = (): void => {
  if (widgetRoot) {
    return
  }

  ensureFonts()
  initSentry()
  if (!window.ri2?.startCollapsed) {
    applySlideoutLayout()
  }

  // In slideout mode, mount directly into the host element that the page layout
  // has reserved as a flex sibling of <section>. This lets Bootstrap containers
  // inside <section> genuinely reflow (flex + min-width:0) rather than being
  // overlapped by a fixed-position panel.
  const slideoutHost =
    window.ri2?.displayMode === "slideout"
      ? document.getElementById("ri2-slideout-host")
      : null

  if (slideoutHost) {
    widgetRoot = ReactDOM.createRoot(slideoutHost)
  } else {
    if (!widgetContainer) {
      widgetContainer = document.createElement("div")
      widgetContainer.id = "ri2-widget-root"
      document.body.appendChild(widgetContainer)
    }
    widgetRoot = ReactDOM.createRoot(widgetContainer)
  }

  widgetRoot.render(
    <React.StrictMode>
      <ChatWidget />
    </React.StrictMode>,
  )
}

export const unmountWidget = (): void => {
  if (!widgetRoot) {
    return
  }

  widgetRoot.unmount()
  widgetRoot = undefined
  removeSlideoutLayout()
}

// ---- Widget collapse / expand (dispatches events that ChatWidget listens for) ----

export const collapseWidget = (): void => {
  window.dispatchEvent(new Event("ri2:collapse"))
}

export const expandWidget = (
  initialMessageOrPayload?: ExpandWidgetInput,
): void => {
  const starterPayload = normalizeStarterBootstrapPayload(
    initialMessageOrPayload,
  )

  if (starterPayload) {
    const existingRi2 = (window.ri2 ?? {}) as NonNullable<Window["ri2"]>
    window.ri2 = {
      ...existingRi2,
      initialMessage: starterPayload.starterMessage,
      initialStarterPayload: starterPayload,
    }
  }

  window.dispatchEvent(
    new CustomEvent("ri2:expand", {
      detail: starterPayload,
    }),
  )
}

// ---- Boot logic ----

const headless = !!window.ri2?.headless
const entryAppElement = document.getElementById("ri2-entry-app")
const widgetElement = document.getElementById("ri2-widget")

// Mount the chat widget if its container exists, or as the default
// when no other entry point is present.
// In headless mode the host page controls mounting entirely via RI2.open().
if (!headless && (widgetElement || (!entryAppElement && !SKIP_LANDING_PAGE))) {
  mountWidget()
}

// Mount the full-screen app or landing page alongside the widget
if (SKIP_LANDING_PAGE) {
  open()
} else if (!headless && entryAppElement) {
  const landingPageRoot = ReactDOM.createRoot(entryAppElement)

  landingPageRoot.render(
    <React.StrictMode>
      <LandingPage open={open} />
    </React.StrictMode>,
  )
}
